// IB1-416 : protocole HTTP/HTTPS piloté par une variable d'environnement plutôt que codé en dur,
// pour éviter le patch manuel (sed) du bundle JS buildé lors des déploiements clients.
const isSecure = process.env.REACT_APP_API_IS_SECURE === 'true';
const httpProtocol = isSecure ? 'https' : 'http';
const wsProtocol = isSecure ? 'wss' : 'ws';

const endpoints = {
  baseURL: `${httpProtocol}://${process.env.REACT_APP_API_BASE_URL}/api/v1`,
  wssURL: (accessToken: string) =>
    `${wsProtocol}://${process.env.REACT_APP_API_BASE_URL}/client-websocket/alerts?authorization=Bearer ${accessToken}`,

  // Session endpoints
  login: '/auth/authenticate',
  validatePhone: '/auth/phone/validate',
  otpResend: '/auth/resend/code',
  otpPayment: '/auth/2fa/phone',
  verifyOtp: '/auth/verify',
  createMfaChallenge: '/auth/mfa/challenges',
  verifyMfaChallenge: (id: string) => `/auth/mfa/challenges/${id}/verify`,
  resendMfaChallenge: (id: string) => `/auth/mfa/challenges/${id}/resend`,
  createAuthChallenge: '/auth/mfa/auth/challenges',
  verifyChallenge: (id: string) => `/auth/mfa/auth/challenges/${id}/verify`,
  resendChallenge: (id: string) => `/auth/mfa/auth/challenges/${id}/resend`,
  logout: '/auth/logout',
  getSesions: '/client/clients/sessions',
  getCbsState: '/client/health-check/cbs',
  closeOtherSessions: '/auth/terminate-sessions',

  // User endpoints
  forgotPass: '/auth/send/code',
  change2Fa: '/auth/mfa',
  updateMFA: '/auth/mfa/state',
  getMe: '/client/clients/current',
  changeUserPassword: '/auth/password',
  updateAutoRenewSub: '/client/subscriptions',
  changeUserLanguage: '/client/clients',
  readAlert: (alertId: string) => `/client/alerts/${alertId}`,
  getAlerts: '/client/alerts',
  readAll: '/client/alerts/read-all',
  userSettings: '/client/settings',
  initTOTP: '/auth/totp/init-setup',
  finalizeTOTOSetup: '/auth/totp/finalize-setup',
  getUnusedBackupCodes: '/auth/totp/backup-codes',
  regenerateBackupCodes: '/auth/totp/backup-codes',
  updateTotpStatus: '/auth/totp/status',
  getTotpStatus: '/auth/totp/status',
  deleteTotpDevice: '/auth/totp',

  // Sub Users endpoints
  getSubUsers: '/client/sub-users',
  destroySubUser: (id: string) => `/client/sub-users/${id}`,
  createSubUser: '/auth/sub-users/register',
  createMainSubUser: '/auth/main-sub-users/register',
  editSubUser: (id: string) => `/client/sub-users/${id}`,
  resetSubUserPassword: (id: string) => `/auth/sub-users/${id}/reset-password`,
  changeSubUserState: (id: string) => `/client/sub-users/${id}/state`,
  getSubUserCredentialsPdf: '/auth/admin/user/credentials/pdf',

  // User accounts endpoints
  getAccounts: '/client/accounts',
  printAccountReport: '/client/accounts/pdf',

  // Statements endpoints
  getStatementHistories: '/transaction/statement-histories',
  downloadStatementHistory: (id: string) => `/transaction/statement-histories/${id}/download`,

  // Transactions endpoints
  getTransactions: '/transaction/transactions',
  getReportTransactions: '/transaction/transactions/report',
  getPendingTransactions: '/payment/financial-operations/histories',
  getTxPendingReport: (id: string) => `/payment/financial-operations/histories/${id}/pdf`,
  getTxReport: '/transaction/transactions/export',
  getCategories: '/transaction/categories',
  getTotalAmounts: '/transaction/statistics/income-expenses',
  getFinancialOverviewStatistic: '/transaction/statistics/financial-overview',
  getExpenseBreakdownStatistic: '/transaction/statistics/expense-breakdown',
  getTxToken: '/transaction/transactions/:ID/download',
  downloadTxQrReport: '/transaction/transactions/download',
  getSubUserTransaction: '/transaction/transactions/sub-users/filter',
  getPendingTransDetails: (id: string) => `/payment/financial-operations/histories/${id}/details`,
  getPendingTransComplianceDoc: (id: string) => `/payment/financial-operations/histories/${id}/compliance-documents`,

  // Transactions validations
  getFinancialOperations: '/payment/financial-operations/orders',
  getFinancialOperationsSelf: '/payment/financial-operations/orders/self',
  getBulkFinancialOperations: '/payment/financial-operations/orders/bulk-list',
  getMyBulkFinancialOperations: '/payment/financial-operations/orders/bulk-list/self',
  approveOperation: '/payment/financial-operations/orders/approve',
  declineOperation: '/payment/financial-operations/orders/decline',

  // Beneficiaries endpoints
  getBeneficiaries: '/client/beneficiaries',
  getDataDetails: '/client/bank-info',
  getBeneficiariesDetails: '/client/beneficiaries/details',

  // Payments endpoints
  createNewPayment: '/payment/payments',
  getExchangeRates: '/payment/currencies/rates',
  createRecurringPayment: '/payment/recurring-payments',
  getStandingOrders: '/payment/recurring-payments',
  editRemoveStandingOrder: (id: string) => `/payment/recurring-payments/${id}`,
  createNewNraPayment: '/payment/taxes',

  // Transfer endpoints
  newTransfer: '/payment/transfers',
  newCsvTransfer: '/payment/transfers/csv',
  newInternationalTransfer: '/payment/international-transfers',
  validInternationalTransfer: '/payment/international-transfers/validate',
  getTransfers: '/payment/transfers/deferred',
  getSwiftTransfers: '/payment/swift-orders',
  getSwiftFile: (id: string) => `/payment/swift-orders/${id}/file`,
  editRemoveTransfer: (id: string) => `/payment/transfers/${id}/deferred`,
  getRatesReport: '/payment/currencies/rates/pdf',
  calculateTransfersFee: '/payment/fees/calculate',
  checkDuplicateTransfer: '/payment/transfers/check-duplicates',
  checkDuplicateSwiftTransfer: '/payment/international-transfers/check-duplicates',
  getAccountInf: '/client/clients/info/account',
  getAccountsInf: '/payment/transfers/multiple/validate',
  getPaymentDetails: (id: string) => `/payment/payments/${id}`,
  recentPayments: '/payment/payments/last',

  // NRA endpoints
  validateNewNRAPayment: '/payment/taxes/validate',
  confirmNRAPayment: '/payment/taxes/confirm-payment',

  // Loans endpoints
  getLoans: '/payment/loans',
  getLoanDetails: '/payment/loans/:ID/payments',

  // Tickets endpoints
  getTickets: 'client/tickets/user',
  createTicket: 'client/tickets',
  deleteTicket: (id: string) => `client/tickets/${id}`,
  editTicket: (id: string) => `client/tickets/${id}`,
  getTicketFile: (id: string) => `client/tickets/${id}/file`,

  // Bills endpoints
  getAllBills: '/payment/subscription-bills',
  payForBill: (id: string) => `/payment/subscription-bills/${id}/pay`,

  // Checkbooks
  // IB1-300 (épique #5428, F1.2/F5.1) : confirmé par Clinton (backend, 11/09) — contrairement aux
  // autres modules d'historique (getLoans, getStandingOrders, ...), '/client/checkbooks/requests'
  // n'accepte QUE POST (création) ; un GET dessus renvoie 405. L'historique du client passe par un
  // endpoint dédié : '/client/checkbooks/requests/current' (mêmes query params page/size/sort).
  fetchCheckbooks: '/client/checkbooks/requests/current',
  requestCheckbook: '/client/checkbooks/requests',
  checkbookFees: (checkbookTypeCode: string) => `/client/checkbooks/types/${checkbookTypeCode}/fees`,
  cancelCheckbook: (id: string) => `/client/checkbooks/requests/${id}/cancel`,
  approvCheckbook: '/client/checkbooks/approve',
  decliCheckbook: '/client/checkbooks/decline',
  // IB1-302/IB1-309 (épique #5428, F5.1) : pas d'endpoint dédié — confirmé par le backend
  // (2026-09-01) que la liste (fetchCheckbooks) retourne déjà le détail complet par élément. Le
  // panneau "Request Details" consomme directement l'élément de liste, voir CheckbookRequestDetail.
  // IB1-318 (épique #5428, F2.1) : pas d'endpoint dédié — confirmé par le backend (2026-09-01),
  // et par investigation code, que l'annuaire des agences est déjà chargé app-wide au démarrage de
  // l'app via `GET /client/bank-info` (`getDataDetails` ci-dessus, `state.data.branches`). Le
  // sélecteur "Pickup Branch" consomme directement cet annuaire, voir PickupBranchField.
  // IB1-319 (épique #5428, F2.1) : pas de pré-remplissage automatique de "Delivery address" —
  // décision produit (2026-09-01), le client saisit intégralement le formulaire structuré. Voir
  // DeliveryOptionField, mémoire projet checkbook_backend_contract_review.

  // Cards
  requestCard: '/client/card-requests',
  fetchCustomCards: '/client/custom-cards',
  fetchCardDesign: (id: string) => `/client/custom-cards/${id}/design`,
  getCardInfo: '/card/cards/info',

  // Saving plans endpoints
  getSavingPlans: '/client/saving-plans',
  createSavingPlan: '/client/saving-plans',

  // Deposits endpoints
  getDepositTypes: '/payment/deposits/types',

  // Currency exchange endpoints
  getAllCurrencyExchangeRequests: '/payment/currency-exchange-requests',

  // Public settings endpoints
  getPublicSettings: '/settings/public/info',
  getPublicAuthUserSettings: '/settings/public/user',

  // Terms and Conditions endpoints
  getPublishedTermsAndConditions: '/settings/terms-and-conditions/published',
  acceptTermsAndConditions: '/client/terms-and-conditions/accept',

  // FAQ endpoints
  getPublishedFaq: '/settings/faqs/published',

  // Idempotency endpoints
  createIdempotencyKey: '/client/idempotency-keys',

  // Card Recharge endpoints (AFBCARD)
  submitCardRecharge: '/card/cards/recharge',
  approveCardRecharge: '/card/cards/recharge/approve',
  declineCardRecharge: '/card/cards/recharge/decline',
  getPendingRecharges: '/card/cards/recharge/pending',
};

export default endpoints;
